import React, { ChangeEvent, useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  Box,
  Button,
  Dialog,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Mail, Smartphone } from "lucide-react";
import { useForm } from "react-hook-form";
import CustomTextField from "../common/CustomTextField";
import OTPVerification from "../2FA/OTPVerification";
import {
  Generate2faRequest,
  TwoFactorSaveRequest,
  fetchAllUserTwoFactorList,
  generate2faOTP,
  reset2FAFetchStatus,
  save2faCommunicationData,
  useTwoFactorReducer,
  validate2faOTP,
} from "../../redux/slice/2FA/twoFactorSlice";
import { useAppDispatch } from "../../redux/slice/redux-hooks";
import {
  MainErrorInfo,
  SingleAlertInfo,
  hideSingleAlert,
  setMainErrorObj,
  setSingleAlertObj,
} from "../../redux/slice/commonSlice";
import { TWO_FACTOR_VERIFICATION_CODE_LENGTH } from "../../constants/applicationConstants";
import PhoneInputField from "../common/PhoneInputField";
import CustomCheckbox from "../common/CustomCheckBox";
import { useModalStyles } from "../common/useModalStyles";

export type verificationForm = {
  account: string | undefined;
};

interface VerificationDialogProps {
  open: boolean;
  onClose: () => void;
  type: "email" | "phone";
}

const VerificationDialog: React.FC<VerificationDialogProps> = ({
  open,
  onClose,
  type,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    tokens,
    paperSx,
    headerSx,
    iconBoxSx,
    bodySx,
    footerSx,
    closeIconSx,
    inputFieldSx,
    inputFieldWithLabelSx,
    primaryBtnSx,
    cancelBtnSx,
  } = useModalStyles();

  const {
    control,
    handleSubmit,
    reset,
    setValue: setFormValue,
  } = useForm<verificationForm>();
  const [value, setValue] = useState("");
  const [otp, setOtp] = useState("");
  const [disable, setDisable] = useState(false);
  const [isAcknowledgementChecked, setAcknowledgementChecked] = useState(false);

  const {
    generate2faStatus,
    validate2faStatus,
    save2faOptionStatus,
    User2FaListInfo,
  } = useTwoFactorReducer();

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (open) {
      if (generate2faStatus === "success") {
        const alertObj: SingleAlertInfo = {
          message: "successfully sent the OTP code.",
          alertType: "success",
        };
        dispatch(setSingleAlertObj(alertObj));
      } else if (generate2faStatus === "error") {
        const alertObj: SingleAlertInfo = {
          message: "error while sending OTP code",
          alertType: "error",
        };
        dispatch(setSingleAlertObj(alertObj));
      }
      // setTimeout(() => {   // CS-1984: capture id + cleanup below
      const hideTimerId = setTimeout(() => {   // CS-1984
        dispatch(hideSingleAlert());
      }, 5000);
      return () => clearTimeout(hideTimerId);   // CS-1984
    }
  }, [generate2faStatus]);

  useEffect(() => {
    if (open) {
      if (validate2faStatus === "success") {
        let obj: Generate2faRequest = {
          methodValue: value,
          methodType: type === "email" ? "EMAIL" : "MOBILE",
          isPrimary: false,
        };
        dispatch(save2faCommunicationData(obj));
      } else if (validate2faStatus === "error") {
        const alertObj: SingleAlertInfo = {
          message: "error while validate the OTP",
          alertType: "error",
        };
        dispatch(setSingleAlertObj(alertObj));
      }
      // setTimeout(() => {   // CS-1984: capture id + cleanup below
      const hideTimerId = setTimeout(() => {   // CS-1984
        dispatch(hideSingleAlert());
      }, 5000);
      setAcknowledgementChecked(true);
      return () => clearTimeout(hideTimerId);   // CS-1984
    }
  }, [validate2faStatus]);

  useEffect(() => {
    if (open) {
      if (save2faOptionStatus === "success") {
        const alertObj: SingleAlertInfo = {
          message: "successfully added two factor option",
          alertType: "success",
        };
        dispatch(setSingleAlertObj(alertObj));
        closePopup();
        dispatch(fetchAllUserTwoFactorList(true));
      }
      // setTimeout(() => {   // CS-1984: capture id + cleanup below
      const hideTimerId = setTimeout(() => {   // CS-1984
        dispatch(hideSingleAlert());
      }, 5000);
      setValue("");
      setDisable(false);
      return () => clearTimeout(hideTimerId);   // CS-1984
    }
  }, [save2faOptionStatus]);

  const closePopup = () => {
    onClose();
    reset();
    setValue("");
    setOtp("");
    setDisable(false);
    setAcknowledgementChecked(false);
    dispatch(reset2FAFetchStatus());
  };

  const handleSend = () => {
    let obj: TwoFactorSaveRequest = {
      communicationValue: value,
      communicationType: type === "email" ? "EMAIL" : "MOBILE",
      userId: 0,
    };
    setDisable(true);
    dispatch(generate2faOTP({ request: obj }));
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };

  const isAlreadyRegistered = () => {
    const entered = value.trim();
    if (!entered) return false;
    // Compare the last 10 digits so a country-code prefix (e.g. entered "+1 ..."
    // = 11 digits vs a stored 10-digit number) doesn't cause a false mismatch.
    const last10Digits = (s: string) => (s ?? "").replace(/\D/g, "").slice(-10);
    return (User2FaListInfo ?? []).some((method) => {
      if (type === "email") {
        return (
          method.methodType === "EMAIL" &&
          (method.methodValue ?? "").trim().toLowerCase() ===
            entered.toLowerCase()
        );
      }
      return (
        method.methodType === "MOBILE" &&
        last10Digits(method.methodValue) === last10Digits(entered)
      );
    });
  };

  const handleVerify = () => {
    if (isAlreadyRegistered()) {
      const errorObj: MainErrorInfo = {
        title: "An error is occurred while communicating with the server.",
        errorMessageStream: `This ${
          type === "email" ? "EMAIL" : "MOBILE"
        } is already registered for 2FA`,
      };
      dispatch(setMainErrorObj(errorObj));
      return;
    }
    dispatch(validate2faOTP({ otp: otp }));
  };

  const onSubmit = () => {
    handleSend();
  };

  const handleCheckboxChange = (event: { target: { checked: boolean } }) => {
    const checked = event.target.checked;
    setAcknowledgementChecked(checked);
  };

  const handleDialogContent = () => {
    const twoFAtype = type === "email" ? "email address" : "mobile number";

    return (
      <Grid container spacing={2} alignItems="flex-start">
        <Grid item>
          <CustomCheckbox
            control={control}
            controllerName="acknowledgement"
            label=""
            onChange={handleCheckboxChange}
            checked={isAcknowledgementChecked}
            sx={{ p: 0, m: 0 }}
          />
        </Grid>
        <Grid item xs>
          <Typography
            variant="body2"
            sx={{ color: tokens.textPrimary, marginBottom: 2 }}
          >
            By providing your {twoFAtype}, you authorize us to use it for 2FA.
            To stop receiving 2FA on this {twoFAtype}, remove it from your
            account profile.
            {type === "email" ? "" : " Standard messaging rates may apply."}{" "}
            {type === "phone" && (
              <Typography component="span" fontWeight="bold">
                User also agrees to the{" "}
                <a
                  href="/terms-2fa.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    textDecoration: "underline",
                    color: tokens.accent,
                    fontWeight: "bold",
                  }}
                >
                  2FA SMS T&C
                </a>
              </Typography>
            )}
          </Typography>
        </Grid>
      </Grid>
    );
  };

  useEffect(() => {
    if (!open) {
      reset();
      setValue("");
      setOtp("");
      setDisable(false);
      setAcknowledgementChecked(false);
      dispatch(reset2FAFetchStatus());
    }
  }, [open, reset, dispatch]);

  const subtitle =
    type === "email"
      ? "Add and verify an email address for two-step authentication."
      : "Add and verify a mobile number for two-step authentication.";

  return (
    <Dialog
      open={open}
      onClose={closePopup}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: paperSx(isMobile) }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={headerSx}>
          <Box sx={iconBoxSx(tokens.accent)}>
            {type === "email" ? (
              <Mail size={20} color={tokens.accent} strokeWidth={1.8} />
            ) : (
              <Smartphone size={20} color={tokens.accent} strokeWidth={1.8} />
            )}
          </Box>
          <Box>
            <Typography
              fontWeight={700}
              sx={{
                fontSize: "1.1rem",
                color: tokens.textPrimary,
                lineHeight: 1.3,
              }}
            >
              Two-Step Authentication
            </Typography>
            <Typography
              sx={{
                fontSize: "0.8rem",
                color: tokens.textSecondary,
                mt: 0.15,
              }}
            >
              {subtitle}
            </Typography>
          </Box>
          <IconButton onClick={closePopup} size="small" sx={closeIconSx}>
            <CloseIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Box>

        <Box sx={bodySx}>
          {handleDialogContent()}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1.5,
              alignItems: { sm: "flex-start" },
            }}
          >
            {type === "phone" && (
              <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
                <Box sx={inputFieldSx()}>
                  <PhoneInputField
                    control={control}
                    name="Phone"
                    label="Phone"
                    value={value}
                    isDisabled={!isAcknowledgementChecked}
                    onChange={handleChange}
                  />
                </Box>
              </Box>
            )}
            {type === "email" && (
              <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
                <Box sx={inputFieldWithLabelSx()}>
                  <CustomTextField
                    control={control}
                    controllerName={"Email"}
                    onChange={handleChange}
                    value={value}
                    label={"Email"}
                    placeholder={"Enter email"}
                    variant="outlined"
                    fullWidth
                    disabled={!isAcknowledgementChecked}
                    rules={
                      type === "email"
                        ? {
                          required: "Email is required.",
                          pattern: {
                            value:
                              /^([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}|n\/a|N\/A)$/i,
                            message: "Invalid email.",
                          },
                        }
                        : {}
                    }
                  />
                </Box>
              </Box>
            )}
            <Button
              variant="contained"
              type="submit"
              disabled={value.trim().length === 0 || disable}
              sx={{
                ...primaryBtnSx,
                alignSelf: { xs: "stretch", sm: "flex-start" },
                mt: { sm: 0.25 },
                minWidth: { sm: 140 },
              }}
            >
              Send Code
            </Button>
          </Box>

          <Box
            sx={{
              mt: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Typography
              component="div"
              sx={{
                fontSize: "0.9rem",
                fontWeight: 600,
                color: tokens.textPrimary,
                textAlign: "center",
                mb: 2,
                maxWidth: 500,
                px: 1,
                width: "100%",
              }}
            >
              Please enter the 6 digit code that you received on your{" "}
              {type === "email" ? "email " : "phone "}
              below
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "100%",
                maxWidth: 400,
              }}
            >
              <OTPVerification
                control={control}
                sendOtp={handleSend}
                submitCode={(v) => setOtp(v)}
                disable={generate2faStatus !== "success"}
                setValue={setFormValue}
              />
            </Box>
          </Box>
        </Box>

        <Box sx={footerSx}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1.5,
            }}
          >
            <Button
              fullWidth
              variant="contained"
              type="button"
              onClick={handleVerify}
              disabled={
                otp.trim().length !== TWO_FACTOR_VERIFICATION_CODE_LENGTH
              }
              sx={primaryBtnSx}
            >
              Verify
            </Button>
            <Button
              fullWidth
              variant="outlined"
              type="button"
              onClick={closePopup}
              startIcon={<CancelIcon sx={{ fontSize: "1rem !important" }} />}
              sx={cancelBtnSx}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </form>
    </Dialog>
  );
};

export default VerificationDialog;
